import React, { CSSProperties, useContext, useEffect, useMemo, useRef, useState } from 'react';
// Felt centre watermark. Points at the shared brand wordmark (same asset as the
// lobby/table headers) so the table logo matches the rest of the app. It's
// rendered as a white silhouette (filter: brightness(0) invert(1) + overlay
// blend), so only the shape matters — the source's own colours are discarded.
import tableLogo from 'assets/images/hjlogo.png';
import { TABLE_DOWN_SHIFT_PCT } from 'constants/gameConstants';
import { getTableTheme } from 'constants/tableThemes';
import { AnimationActivityProvider } from 'contexts/AnimationActivityContext';
import { DealerAnimationProvider } from 'contexts/DealerAnimationContext';
import { DealerChipProvider } from 'contexts/DealerChipContext';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import { PovCardsProvider } from 'contexts/PovCardsContext';
import { SeatRotationProvider } from 'contexts/SeatRotationContext';
import TableSettingsContext, {
  TABLE_VIEW_SCALE_DEFAULT,
  TABLE_VIEW_SCALE_MAX,
  TABLE_VIEW_SCALE_MIN
} from 'contexts/TableSettingsContext';
import { useAppSelector } from 'hooks';
import { useContainerSize } from 'hooks/useContainerSize';
import {
  seatRingLayoutFor,
  seatRingPositions,
  useEllipticalPositions
} from 'hooks/useEllipticalPositions';
import { useIsMobile } from 'hooks/useIsMobile';
import { useOrientation } from 'hooks/useOrientation';
import { useResponsiveScale } from 'hooks/useResponsiveScale';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { useSnapOnArrival } from 'hooks/useTableSnap';
import { useTableUsable } from 'hooks/useTableUsable';
import { TableZ } from 'styles/zIndex';
import { isBalancingMoveTable } from 'utils/recentlyExitedTables';
import { registerTable, setTableHydrated, unregisterTable } from 'utils/tableHydrationState';

import SitGoWaitingPanel from 'components/SitGoWaiting/SitGoWaitingPanel';
import {
  isVoteOpen,
  parseVotePhaseKey,
  votePhaseKey
} from 'components/UserActions/MultiRunActions/votePresentation';

import Bets from './Bets';
import { resolveBoardScale } from './boardScale';
import { DealerButton as OuterDealerButton } from './DealerButton';
import FeltMeta from './FeltMeta';
import { minSeatScaleAcrossRings, resolveFeltScale, SUPPORTED_SEAT_COUNTS } from './feltScale';
import { feltLogoWidth } from './feltTypography';
import HoldEmCards, { getRunKeys } from './HoldEmCards';
import MultiRunVoteText from './MultiRunVoteText';
import Pot from './Pot';
import Seats from './Seats';
import TableLoadIndicator from './TableLoadIndicator';
import TableWinnerMessage from './TableWinnerMessage';

import feltVoteStyles from './multiRunVoteFelt.module.scss';

const DEFAULT_GLOW = '#e9b43c';

/**
 * How far past its design size the felt may grow to fill a large display.
 *
 * Paired with `containerStyle`'s max box: the container decides how much room
 * the felt may occupy, this decides how large the furniture inside it may get.
 * Both used to stop at the design size, which is why 4K and 5K displays got
 * nothing for their extra pixels.
 */
const FELT_FIT_MAX = 1.45;

/**
 * EN-10520 — drop a percentage anchor by TABLE_DOWN_SHIFT_PCT.
 *
 * The whole table moves down together: felt oval, apron, board column, winner
 * banner, and (via useEllipticalPositions' tableCenterY) the seat ring, the bet
 * chips and the dealer button. Doing it through one helper is the point — the
 * felt and the seats are positioned by two different systems, and drifting them
 * apart would put the seats off the rail.
 *
 * `bottom` anchors move the OTHER way, hence `shiftUp` for those.
 */
/**
 * The drop is applied through a CSS variable rather than baked into each value,
 * because it is GATED: only mobile portrait gets the reworked seat ring and
 * table position (see useEllipticalPositions' SeatRingLayout). Every other
 * viewport sets `--table-shift: 0%` and these resolve to the original numbers
 * exactly — no second set of style constants to keep in sync.
 *
 * When the shift is switched OFF entirely they emit the plain literal instead of
 * a `calc()` that can only ever evaluate to it. The felt has ~10 of these
 * anchors and this surface already runs ~120 style recalcs/sec at idle
 * (EN-10462), so there is no reason to make the engine resolve a variable to
 * reach a number known at module load. The branch is on a module constant, so it
 * is decided once, and the knob still works the moment the constant is non-zero.
 */
const TABLE_SHIFT_IS_ACTIVE = TABLE_DOWN_SHIFT_PCT !== 0;

const shiftDown = (basePct: number): string =>
  TABLE_SHIFT_IS_ACTIVE ? `calc(${basePct}% + var(--table-shift, 0%))` : `${basePct}%`;
const shiftUp = (basePct: number): string =>
  TABLE_SHIFT_IS_ACTIVE ? `calc(${basePct}% - var(--table-shift, 0%))` : `${basePct}%`;

// Where the community board's own centre sits, per orientation (the felt's
// vertical midline; the seat ellipse uses the same numbers).
const BOARD_CENTER_PCT = 50;
const PORTRAIT_BOARD_CENTER_PCT = 46;

/**
 * Append an alpha byte to a #RRGGBB hex string. Exported for unit testing.
 */
export const withAlpha = (hex: string, alpha: number): string => {
  const cleaned = hex.replace('#', '');
  // Expand short-hand (e.g. #abc → aabbcc) for safety.
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${full}${a}`;
};

const Table = () => {
  const guid = useAppSelector((state) => state.user.playerGUID);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const players = useTableSelector((table) => table.players);
  // Empty until the first table state (gotTexas/gotOmaha) lands → show the HJ
  // chip load indicator (below), flat and centered over the felt.
  const tableType = useTableSelector((table) => table.game.tabletype);
  // A run-it-twice fork stacks a board per run. Portrait floats the pot above
  // the board, and the winner banner rides the TOP row while later boards are
  // being paid — taller than a row, so it needs felt above that row to spill
  // into rather than covering the board below. Lift the pot while the fork is
  // up to make that room (TableWinnerMessage::bannerRowShift). Landscape parks
  // the pot beside the board and already has the room.
  const isMultiRunActive = useTableSelector((table) => table.currentPlayer.isMultiRunActive);
  const multiRunCards = useTableSelector((table) => table.game.multiRunCards);
  const runs = useTableSelector((table) => table.game.runs);
  // EN-10856: the multi-run vote's felt state, as one primitive so the root
  // re-renders only when the phase or the offered count changes.
  const vote = parseVotePhaseKey(useTableSelector((table) => votePhaseKey(table.game)));
  const voteOpen = isVoteOpen(vote);
  const hasFork = isMultiRunActive && getRunKeys(multiRunCards, runs).length > 1;
  // Balancing-DESTINATION caption. This cell is the balanced-to table if its
  // gameID has the balancing mark set (written only for the destination in the
  // balancing branch of TournamentScreenModal.tsx; not by Re-Entry, not for
  // the source). Poll while the mark could still be valid — the mark may be
  // written after the cell mounts (grid rebind order, single-table nav timing)
  // and this component's tableIdForBalancing changes on rebind. Not gated on
  // tableType — a destination that was pre-subscribed by App.tsx's auto-resub
  // arrives already hydrated (tableType truthy from the first render), and we
  // want the caption to render there too. The mark's short grace + explicit
  // clearBalancingMoveTable after the source's setTimeout completes keeps the
  // overlay from lingering over an active hand.
  const tableIdForBalancing = useTableId();
  const [isBalancingDestination, setIsBalancingDestination] = useState(() =>
    isBalancingMoveTable(tableIdForBalancing)
  );
  useEffect(() => {
    setIsBalancingDestination(isBalancingMoveTable(tableIdForBalancing));
    const id = window.setInterval(() => {
      setIsBalancingDestination(isBalancingMoveTable(tableIdForBalancing));
    }, 400);
    return () => window.clearInterval(id);
  }, [tableIdForBalancing]);
  const { setSeatPositions, setBetPositions } = useContext(GameContext);
  const { getThemeForGame, tableViewScale } = useContext(TableSettingsContext);
  // This table's effective theme = its per-table override (set from the in-table
  // settings) if any, else the player's global "All My Tables" theme.
  const gameID = useTableId();
  const theme = useMemo(() => getTableTheme(getThemeForGame(gameID)), [getThemeForGame, gameID]);
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  // EN-10520's seat-ring rework (clearance wedges + the whole-table drop) ships
  // to MOBILE PORTRAIT only. Mobile landscape and both desktop orientations keep
  // the original even seat distribution at the original table position. Note the
  // grid forces 'portrait' on its cells via OrientationOverrideContext, but the
  // grid is desktop-only — useIsMobile() is false there, so cells stay classic.
  const isMobile = useIsMobile();
  const ringLayout = useMemo(() => seatRingLayoutFor(isMobile, isPortrait), [isMobile, isPortrait]);

  // Snap-on-arrival. Switching into a table remounts this subtree, so every
  // mount-time animation would otherwise replay actions that happened while the
  // player was on another table (cards dealing in, chips flying, the pot
  // counting up). The window opens here — during render, before any child
  // effect can fire — and closes a painted frame after this table's current
  // state is on screen. See hooks/useTableSnap + utils/tableSnapState.
  useSnapOnArrival(gameID, !!tableType);

  // How loaded is this table, at the two thresholds that matter here:
  //   seatsPending — cosmetic, IMMEDIATE. Seats reported but none drawn yet.
  //   unusable     — consequential, graced. Drives the hydration registry (and
  //                  through it ConnectionManager's reconnect gate).
  // A felt can be fully drawn (pot, blinds, dealer) while every seat reads Open,
  // because seat rows and the server's own player count come from different
  // table columns. See hooks/useTableUsable and utils/tableUsable.
  const { seatsPending, unusable } = useTableUsable();

  // Publish this table's felt-hydration state so ConnectionManager's foreground
  // staleness gate only reconnects when a table the player is viewing genuinely
  // never hydrated — never a live-but-quiet one. See utils/tableHydrationState.
  //
  // Reports `useTableUsable`, NOT the bare `!!tableType` this used to publish. A
  // table whose seat data never arrived is unusable but was reported hydrated,
  // which made ConnectionManager's gate take its "felt quiet but hydrated — no
  // reconnect" branch and left the player with no automatic recovery at all.
  useEffect(() => {
    registerTable(gameID);
    return () => unregisterTable(gameID);
  }, [gameID]);
  useEffect(() => {
    setTableHydrated(gameID, !unusable);
  }, [gameID, unusable]);

  const containerRef = useRef<HTMLDivElement>(null);
  const feltGlowRef = useRef<HTMLDivElement>(null);
  const feltGlowBoxRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);

  // How much of the design size the CONTAINER can afford — the felt's own fit,
  // before the player has any say in it. Reference sizes are the design
  // dimensions at which seats render full-size (fit 1).
  //
  // `max` is above 1 on purpose. It used to be exactly 1, which meant the felt
  // stopped growing at the design size no matter how much room it had: on a 4K
  // or 5K display the table painted at the same size as on a 1440p one, and
  // "Largest" capped out early because there was nothing left to give.
  const feltFit = useResponsiveScale(
    containerSize,
    isPortrait
      ? { referenceWidth: 480, referenceHeight: 900, min: 0.5, max: FELT_FIT_MAX }
      : { referenceWidth: 1400, referenceHeight: 700, min: 0.5, max: FELT_FIT_MAX }
  );

  /**
   * ONE painted scale for the whole seat: what the container can afford, times
   * what the player asked for.
   *
   * This is the ticket's fix in a single line. Table View Size used to be a
   * separate multiplier applied to a handful of individual elements — board
   * cards and some felt text — which is why it could not change the size of the
   * cards in the player's own hand, and why growing it made things overlap:
   * some elements grew and the space between them did not.
   *
   * Folding it into the seat scale means it multiplies the seat's single
   * `transform: scale()`, so every part of a seat grows together and the
   * RELATIVE geometry inside a seat is identical at every setting. The
   * own-seat clearances verified in Seat/__tests__/cardOcclusion.test.ts are
   * scale-invariant, so they survive this untouched.
   *
   * Crucially it is also the value handed to `useEllipticalPositions` and (via
   * `--seat-scale`) to `seatEdgeSafeLeft`, so bet clearance, dealer clearance
   * and the viewport clamp are all computed from the furniture's REAL painted
   * size. That is what lets the range be widened safely: at a bigger setting
   * the seats are bigger AND further apart, instead of bigger in the same
   * space.
   *
   * Quantised before it reaches the DOM — see FELT_SCALE_QUANTUM.
   */
  const seatCount = players?.slice(1)?.length || 0;

  /**
   * What the RING can afford, independent of what the player asked for.
   *
   * Computed from the seat positions alone — they are pure container
   * percentages with no dependency on the scale, so this is not circular. See
   * feltScale for why this exists at all: without it, raising the Table View
   * Size ceiling would simply make seats overlap at the top of the range, which
   * is the behaviour the old +20% cap was there to prevent.
   */
  /**
   * The ring's own orientation, which is NOT always the seats'.
   *
   * `useEllipticalPositions` derives its ring from the container's aspect ratio,
   * while the seats inside it paint from the orientation CONTEXT — and the grid
   * forces that context to 'portrait' on cells that are physically wider than
   * tall. Measuring the clamp against `isPortrait` therefore sized a portrait
   * ring the hook never built, and came out ~11% under-scale in grid view.
   */
  const ringIsPortrait = containerSize.height > containerSize.width;
  const candidateRings = useMemo(
    () =>
      // EVERY supported ring size, not this table's current one. Sizing against
      // live occupancy made tables in a grid paint at different sizes, and would
      // have resized the felt every time someone sat down. See
      // SUPPORTED_SEAT_COUNTS for why it is the whole set rather than just 9.
      SUPPORTED_SEAT_COUNTS.map((seats) =>
        seatRingPositions(
          seats,
          // Container-derived, matching what the hook derives internally...
          ringIsPortrait,
          // ...but the PADDING comes from the orientation value, because that is
          // what this component passes the hook below. Both have to match the
          // real call or the clamp is sizing a ring nobody renders.
          isPortrait ? 6 : 12,
          isPortrait ? 6 : 12,
          ringLayout
        )
      ),
    [ringIsPortrait, isPortrait, ringLayout]
  );
  const ringScaleLimit = useMemo(
    // Rings from the container, footprint from the seats' own orientation.
    () =>
      minSeatScaleAcrossRings(
        candidateRings,
        containerSize.width,
        containerSize.height,
        isPortrait
      ),
    [candidateRings, containerSize.width, containerSize.height, isPortrait]
  );

  const seatScale = useMemo(() => {
    // Not measured yet — paint at DESIGN size for the first frame.
    //
    // `useResponsiveScale` returns its `max` outright when the container has no
    // measurement ("assume full size to avoid a flash of shrunken content"), and
    // `useContainerSize` seeds at 0x0 and measures in an effect, so every table
    // mount and every table switch renders one frame from that fallback. While
    // `max` was 1 the fallback genuinely meant full size. It is FELT_FIT_MAX
    // now, and multiplied by the player's setting, so honouring it would paint
    // the first frame at up to 2.6x — a visible pop on a felt that settles at 1,
    // and seats overflowing a grid cell that settles at ~0.53. useContainerSize's
    // own docblock already records that failure mode from the last time it
    // happened.
    if (!containerSize.width || !containerSize.height) return 1;

    // See resolveFeltScale: the slider is a position in THIS table's own safe
    // range, so every table responds to it and Largest means "as big as this
    // table can go without anything overlapping".
    return resolveFeltScale({
      feltFit,
      ringScaleLimit,
      tableViewScale,
      viewScaleMin: TABLE_VIEW_SCALE_MIN,
      viewScaleMax: TABLE_VIEW_SCALE_MAX,
    viewScaleDefault: TABLE_VIEW_SCALE_DEFAULT
    });
  }, [feltFit, tableViewScale, ringScaleLimit, containerSize.width, containerSize.height]);

  /**
   * The community board's painted scale — the seats' legibility floor, bounded
   * by the room the flank seats actually leave.
   *
   * Computed HERE and published as `--board-scale` because the bound needs the
   * measured container and the seat ring, neither of which the board itself can
   * see. HoldEmCards keeps the old CSS expression as its fallback, so a board
   * mounted outside a table (stories, unit tests) still paints.
   *
   * See boardScale.ts: without the bound, a phone's board sat at a constant
   * 0.8625 at every Table View Size and crossed the left flank seat's plate by
   * up to 18.2px, which is EN-10740's follow-up report.
   */
  const boardScale = useMemo(() => {
    if (!containerSize.width || !containerSize.height) return undefined;
    return resolveBoardScale({
      rings: candidateRings,
      containerWidth: containerSize.width,
      containerHeight: containerSize.height,
      seatScale,
      isPortrait,
      ringLayout
    });
  }, [
    candidateRings,
    containerSize.width,
    containerSize.height,
    seatScale,
    isPortrait,
    ringLayout
  ]);

  // Size the rotating glow layer to cover the felt oval. A rotated square
  // always covers a centred disk of its own side length, so a side >= the
  // oval's larger dimension guarantees full coverage at every angle. Re-measured
  // on resize / orientation change via the ResizeObserver in useContainerSize.
  const glowBoxSize = useContainerSize(feltGlowBoxRef);
  const glowSpinnerSide = Math.ceil(Math.max(glowBoxSize.width, glowBoxSize.height) * 1.06);

  // Static conic gradient (painted once into the layer; the spin is a
  // transform, so this never re-rasterizes per frame). Colour from the theme.
  const feltGlowConic = useMemo(() => {
    const glow = theme.glowColor ?? DEFAULT_GLOW;
    const glowStrong = withAlpha(glow, 0.6);
    const glowSoft = withAlpha(glow, 0.3);
    return `conic-gradient(from 0deg, transparent 0%, ${glowStrong} 8%, transparent 16%, transparent 50%, ${glowSoft} 58%, transparent 66%)`;
  }, [theme.glowColor]);

  // Rotating border glow on the felt edge. The conic-gradient is painted ONCE
  // into a square layer (see JSX) and the compositor just spins that texture
  // via a transform animation — so there is no per-frame repaint. The old
  // approach re-rasterized the full gradient every frame, which was the table's
  // single biggest sustained GPU cost (and multiplied per table in multi-table
  // grid). A transform animation costs ~the same regardless of table count.
  useEffect(() => {
    const el = feltGlowRef.current;
    if (!el) return;
    if (isHandHistorySnapshot) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce) return; // honour OS "reduce motion" — leave the glow static

    // PERF - stepped rotation via setInterval, on purpose. The previous
    // implementation was an infinite composited WAAPI transform spin: zero
    // repaint, but an always-running animation forces the WebView to produce
    // frames at the display refresh rate for as long as the table is visible
    // (measured: it alone pinned the table at the 60fps cap, ~30-40 CPU pts).
    // Do NOT convert this back to element.animate / CSS animation / GSAP -
    // any continuously-scheduled animation reintroduces that cost. Stepping
    // the transform 10x/sec keeps the same 24 deg/sec drift (2.4 deg/step on
    // a soft-edged gradient reads as smooth) at ~1/6 the frame cost, and the
    // pipeline is free to go fully idle between steps. See
    // docs/investigations/20260819-game-client-overheating.md (8d/8e).
    const DEG_PER_SEC = 24; // matches the old 15s/rotation spin
    const STEP_MS = 100;
    let iv: number | null = null;
    const start = () => {
      if (iv !== null) return;
      iv = window.setInterval(() => {
        const deg = ((performance.now() / 1000) * DEG_PER_SEC) % 360;
        el.style.transform = `translate(-50%, -50%) rotate(${deg}deg)`;
      }, STEP_MS);
    };
    const stop = () => {
      if (iv !== null) {
        window.clearInterval(iv);
        iv = null;
      }
    };
    start();

    // Freeze the spin while the table isn't on screen (e.g. an off-screen
    // multi-table cell) — a cleared interval costs nothing. Note this is
    // viewport intersection only; it won't catch a table fully occluded by a
    // modal, which the browser's own timer throttling largely handles.
    const box = feltGlowBoxRef.current;
    let io: IntersectionObserver | null = null;
    if (box && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) start();
          else stop();
        },
        { rootMargin: '100px' }
      );
      io.observe(box);
    }

    return () => {
      stop();
      io?.disconnect();
    };
  }, [isHandHistorySnapshot]);

  // `seatCount` is declared above, with the felt scale — the ring it describes
  // has to be known before the scale can be clamped to it.
  const { seatPositions, betPositions, sampler } = useEllipticalPositions(
    seatCount,
    containerSize,
    isPortrait ? 6 : 12, // seat padding — higher = closer to table center (overlapping rail)
    isPortrait ? 16 : 18, // bet lerp toward center (landscape only) — higher = further from seat
    isPortrait ? 6 : 12, // vertical seat padding — matches the horizontal one
    // Flank-seat bets size their clearance off the seat furniture's real
    // painted size, which is this scale — see seatClearancePct.
    seatScale,
    // The reworked ring (clearance wedges + table drop) is mobile portrait ONLY;
    // every other viewport keeps the original even distribution and no drop.
    ringLayout,
    // How much bigger the furniture is than the regime the hand-tuned bet
    // offsets were measured in. Those offsets are container PERCENTAGES tuned
    // when `--seat-scale` was exactly the container fit; folding the player's
    // setting into that scale breaks them, and measurably so — at a 1.05x
    // multiplier on a 390x740 portrait table the POV neighbours' pills are
    // already on their own dealer markers. 1 when the player is at the size the
    // tuning assumed, so nothing moves there.
    feltFit > 0 ? seatScale / feltFit : 1
  );

  useEffect(() => {
    setSeatPositions(seatPositions);
    setBetPositions(betPositions);
  }, [seatPositions, betPositions, setSeatPositions, setBetPositions]);

  const overlayRef = useRef<HTMLDivElement>(null);

  const themedFeltGradient = useMemo(() => {
    const [c0, c1, c2, c3, c4] = theme.feltGradient;
    return `radial-gradient(ellipse at 50% 40%, ${c0} 0%, ${c1} 30%, ${c2} 55%, ${c3} 80%, ${c4} 100%)`;
  }, [theme.feltGradient]);

  const themedOval: CSSProperties = {
    ...(isPortrait ? portraitTableOvalStyle : tableOvalStyle),
    backgroundColor: theme.feltBase
  };
  const themedInnerFelt: CSSProperties = {
    ...innerFeltStyle,
    background: themedFeltGradient
  };

  return (
    <AnimationActivityProvider>
      <DealerChipProvider>
        {/* EN-10420: lets the POV bet pill (Bet.tsx) anchor itself to the POV
            hole cards while the bet/raise tray is open. */}
        <PovCardsProvider>
          <DealerAnimationProvider>
            <SeatRotationProvider seatCount={seatCount} sampler={sampler}>
              <div
                ref={containerRef}
                data-game-table-container='true'
                data-table-root=''
                data-multi-run-vote={voteOpen ? vote.phase : undefined}
                data-multi-run-offer={voteOpen && vote.offer > 0 ? vote.offer : undefined}
                style={{
                  ...(isPortrait ? portraitContainerStyle : containerStyle),
                  // THE painted scale for the felt: container fit x the player's
                  // Table View Size. Everything sized in design px multiplies by
                  // this, and nothing multiplies by the view scale a second time
                  // — see the note on `seatScale` above.
                  ['--seat-scale' as any]: seatScale,
                  // The board's own scale — the seat scale floored for
                  // legibility and then bounded by the room the flank seats
                  // leave. Omitted until the container is measured so
                  // HoldEmCards falls back to its own expression for that one
                  // frame rather than painting at a scale derived from 0x0.
                  ...(boardScale === undefined ? {} : { ['--board-scale' as any]: boardScale }),
                  // Published for diagnostics and for anything that needs the
                  // player's setting WITHOUT the container fit. Deliberately not
                  // used for sizing any more: sizing off both this and
                  // `--seat-scale` is exactly the double-application this change
                  // removed.
                  ['--table-view-scale' as any]: tableViewScale,
                  // The container's own fit, without the player's setting. Same
                  // caveat — diagnostics, not sizing.
                  ['--felt-fit' as any]: feltFit,
                  // The felt OVAL's painted height, as a UNITLESS number of real
                  // px. Unitless because its only consumer divides by it to get
                  // a `scale()` factor, and CSS calc() cannot divide a length by
                  // a length. The fractions mirror the oval's own inset in
                  // tableOvalStyle / portraitTableOvalStyle (20%/20% landscape,
                  // 5%/15% portrait) — keep them in step, or the run-it-twice
                  // fork sizes itself against a felt nobody painted.
                  ['--felt-oval-height' as any]: containerSize.height * (isPortrait ? 0.8 : 0.6),
                  // Drives shiftDown/shiftUp on the felt oval, apron, board
                  // column and winner banner. 0% everywhere except mobile
                  // portrait, so those all resolve to their original positions.
                  ['--table-shift' as any]: `${ringLayout.shift ? TABLE_DOWN_SHIFT_PCT : 0}%`
                }}>
                {/* Showdown overlay */}
                <div ref={overlayRef} style={overlayStyle}></div>

                {/* Table load indicator — spinning HJ chip shown until the first
                  table state lands. Rendered here (OUTSIDE the 3D perspective
                  tilt below) so the round chip faces the camera and isn't
                  squashed into an ellipse — identical to the Tournament Final
                  Balancing spinner. Sizes itself to the surface (full felt vs
                  grid tile) — see TableLoadIndicator. */}
                {/* The union of the two thresholds: `unusable` covers the
                    no-snapshot-at-all case (and the confirmed failure),
                    `seatsPending` covers the ordinary seats-not-drawn-yet
                    window with no grace, so a table that loads in under 5s
                    still shows a spinner. */}
                {(unusable || seatsPending || isBalancingDestination) && (
                  <TableLoadIndicator balancing={isBalancingDestination} />
                )}

                {/* The visible table oval — with 3D perspective tilt.
                    The perspective distance scales with the felt so the tilt
                    looks the same at every size; see feltPerspectivePx. */}
                <div
                  style={{
                    ...perspectiveWrap,
                    perspective: `${feltPerspectivePx(containerSize.height, isPortrait)}px`
                  }}>
                  {/* Table edge/apron — offset down to create visible thickness */}
                  <div style={isPortrait ? portraitApronStyle : apronStyle} />
                  {/* Main table surface */}
                  <div style={themedOval}>
                    {/* Rotating border glow — colour from theme. The box is the oval
                clip; the inner square carries the gradient and is spun by a
                GPU transform animation (see effect above). */}
                    <div ref={feltGlowBoxRef} style={feltGlowStyle}>
                      <div
                        ref={feltGlowRef}
                        style={{
                          ...feltGlowSpinnerStyle,
                          width: glowSpinnerSide,
                          height: glowSpinnerSide,
                          background: feltGlowConic
                        }}
                      />
                    </div>
                    <div style={themedInnerFelt}>
                      {/* Hijack centre watermark — sits on the felt, behind cards and pot */}
                      <img
                        src={tableLogo}
                        alt=''
                        style={isPortrait ? portraitTableLogoStyle : tableLogoStyle}
                      />
                    </div>
                    {/* EN-10856: the vote's felt tint. On the oval, under the
                        content layer below, so it can never cover the board,
                        the pot or a seat. */}
                    <div className={feltVoteStyles.tint} aria-hidden='true' />
                  </div>
                </div>

                {/* Table content — board, pot and felt text. FLAT, and OUTSIDE
                    the perspective wrapper above (EN-11031). Inside it, with the
                    felt's rotateX/preserve-3d, older iPads drew everything here
                    blank while the flat layers below (seats, bets, dealer button)
                    kept painting. Same box as the wrapper (both inset 0 in this
                    container), so the percentages resolve to the same place. */}
                <div style={isPortrait ? portraitTableContentStyle : tableContentStyle}>
                  <div style={isPortrait ? portraitCardsAndPotCol : cardsAndPotRow}>
                    {isPortrait ? (
                      // Anchor the board in flow; float the Pot above and the details
                      // below it (absolute). This keeps the community cards fixed at
                      // the table center no matter how the pot grows (side pots).
                      <div style={portraitBoardWrap}>
                        <div style={hasFork ? portraitPotAboveFork : portraitPotAbove}>
                          <Pot />
                        </div>
                        <HoldEmCards />
                        <div style={portraitDetailsBelow}>
                          <MultiRunVoteText />
                          <FeltMeta />
                        </div>
                      </div>
                    ) : (
                      // Landscape: one centered column (cards → felt meta → pot).
                      // Being the sole flex child, the board stays centered on the
                      // felt; the pot pill anchors itself to the RIGHT of this
                      // column (see Pot's pillWrapperStyle) and the side pots to
                      // its left, so neither is in this flow.
                      <div style={landscapeBoardWrap}>
                        <HoldEmCards />
                        <MultiRunVoteText />
                        <FeltMeta />
                        <Pot />
                      </div>
                    )}
                  </div>
                </div>

                {/* Winner message. One anchor for every hand — a fork's banner
                    nudges itself onto the branch row it belongs to from
                    here (see TableWinnerMessage). */}
                <div style={isPortrait ? portraitWinnerMessageStyle : winnerMessageStyle}>
                  <TableWinnerMessage />
                </div>

                {/* EN-11257: the waiting panel while a Sit & Go fills (flag-gated
                    inside). Beside the seats, in their stacking context, so the
                    seat cards paint over it (TableZ.sitGoWaitingPanel). */}
                {tableType === 't' && !isHandHistorySnapshot && <SitGoWaitingPanel />}

                {/* Seats — positioned as percentages of the container */}
                <Seats guid={guid} />

                {/* Bets and dealer — positioned as percentages */}
                {!isHandHistorySnapshot && (
                  <>
                    <Bets />
                    <OuterDealerButton />
                  </>
                )}
              </div>
            </SeatRotationProvider>
          </DealerAnimationProvider>
        </PovCardsProvider>
      </DealerChipProvider>
    </AnimationActivityProvider>
  );
};

export default Table;

/* ── Landscape (default) ── */

/**
 * The felt's own ceiling, raised from 1400x700.
 *
 * The old cap was the reason "Largest" did nothing on a big display: past
 * 1400x700 the container stopped growing and `feltFit` stopped rising with it,
 * so a 5K screen painted the same felt as a 1440p one and the only remaining
 * lever was a Table View Size that did not reach hole cards.
 *
 * Still capped rather than unbounded — the aspect ratio is fixed at 2:1 and an
 * uncapped felt on an ultrawide would put the seats an unreadable distance
 * apart — but high enough that a 4K/5K display now spends its extra room on
 * bigger cards. Kept in step with FELT_FIT_MAX: the two together set how large
 * the felt can actually get.
 */
export const containerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  maxWidth: '2400px',
  maxHeight: '1200px',
  aspectRatio: '2 / 1',
  margin: 'auto'
};

const tableOvalStyle: CSSProperties = {
  position: 'absolute',
  top: shiftDown(20),
  left: '12%',
  right: '12%',
  bottom: shiftUp(20),
  borderRadius: '999px',
  backgroundColor: '#143021',
  border: '8px solid #1a1a1a',
  boxShadow: `
    inset 0 0 80px rgba(0,0,0,0.5),
    inset 0 0 20px rgba(0,0,0,0.3),
    0 0 0 3px #2a2a2a,
    0 0 0 6px #1a1a1a,
    0 8px 30px rgba(0,0,0,0.6)
  `,
  overflow: 'hidden',
  zIndex: 1,
  transform: 'rotateX(10deg)',
  transformStyle: 'preserve-3d'
};

/**
 * The table content's place in the container, now that it sits beside the
 * perspective wrapper instead of inside it (EN-11031).
 *
 * Both reproduce what the wrapper used to give it, so nothing else on the felt
 * changes order or starts catching taps:
 *  - zIndex 1 is the wrapper's own; the content follows the wrapper in the
 *    DOM, so it still paints over the felt and under everything that was above
 *    the wrapper (overlay, winner banner, seats, bets). Its old zIndex 6 only
 *    ranked it within the wrapper, against the felt and apron.
 *  - pointerEvents none is what it inherited from the wrapper.
 */
const TABLE_CONTENT_LAYER: CSSProperties = {
  zIndex: 1,
  pointerEvents: 'none'
};

const tableContentStyle: CSSProperties = {
  position: 'absolute',
  // Matches the seat ellipse's centreY (useEllipticalPositions.tableCenterY),
  // so the board and the seat ring travel down together.
  top: shiftDown(BOARD_CENTER_PCT),
  left: '12%',
  right: '12%',
  transform: 'translateY(-50%)',
  display: 'flex',
  justifyContent: 'center',
  ...TABLE_CONTENT_LAYER
};

const cardsAndPotRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem'
};

// Landscape: centered column holding the board (cards → details → pot). Being
// position:relative makes it the anchor for the pot's absolutely-positioned
// pill / side-pots, so they sit relative to the board and stay on the felt.
const landscapeBoardWrap: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.1rem'
};

const winnerMessageStyle: CSSProperties = {
  position: 'absolute',
  left: '50%',
  // Anchor banner's bottom edge just above the community cards (which sit
  // centered at the shifted table centre). translateY(-100%) makes the math
  // independent of banner height so adding/removing rows doesn't push it into
  // the cards.
  top: shiftDown(42),
  transform: 'translate(-50%, -100%)',
  zIndex: TableZ.winnerBanner
};

/* ── Portrait ── */

/**
 * NOT CLIPPED — a seat's turn glow must be allowed to bleed past the table
 * (design call, Jason 2026-08-17).
 *
 * Seats sit ON the felt's rim, so the outer half of the acting player's glow
 * falls outside this box. Clipping it sliced the glow off at a hard vertical
 * line, which is the single strongest tell that a grid is four boxes rather
 * than four tables in one room. Bleeding into a neighbour is the accepted
 * trade: a soft halo crossing the gap reads as light, a guillotined one reads
 * as a bug.
 *
 * TWO things were doing the clipping and both had to go. `overflow: hidden` is
 * the obvious one; `contain: strict` is the subtle one, because it expands to
 * `size layout paint style` and PAINT CONTAINMENT CLIPS TO THE PADDING BOX on
 * its own — removing the overflow alone changes nothing. What is kept is the
 * containment that actually pays for itself with four tables mounted: `layout`
 * and `style` still stop one table's reflow invalidating its siblings, and
 * `size` is preserved because this box is explicitly 100%/100% and never sized
 * by its contents. Only `paint` is given up.
 *
 * The landscape container (containerStyle) has never clipped or contained at
 * all, which is why this only ever showed up in the grid — where every cell is
 * forced to portrait. This brings portrait in line with the behaviour landscape
 * has always had.
 *
 * Escaping glows cannot cause layout trouble: they are box-shadows, and
 * box-shadows never contribute to scrollable overflow. Verified in-browser —
 * the row's scrollWidth is unchanged with the glow bleeding across the gap. The
 * grid row (MultiTableGrid gridStyle) remains the outer clip, so nothing
 * escapes the grid itself.
 */
export const portraitContainerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  margin: 'auto',
  contain: 'size layout style'
} as CSSProperties;

const portraitTableOvalStyle: CSSProperties = {
  position: 'absolute',
  top: shiftDown(5),
  left: '10%',
  right: '10%',
  bottom: shiftUp(15),
  borderRadius: '999px',
  backgroundColor: '#143021',
  border: '6px solid #1a1a1a',
  boxShadow: `
    inset 0 0 60px rgba(0,0,0,0.5),
    inset 0 0 15px rgba(0,0,0,0.3),
    0 0 0 2px #2a2a2a,
    0 0 0 4px #1a1a1a,
    0 6px 24px rgba(0,0,0,0.6)
  `,
  overflow: 'hidden',
  zIndex: 1,
  transform: 'rotateX(12deg)',
  transformStyle: 'preserve-3d'
};

const portraitTableContentStyle: CSSProperties = {
  position: 'absolute',
  // Center the board on the table's vertical midline — the same value the seat
  // ellipse uses (useEllipticalPositions.tableCenterY: base 46% plus
  // TABLE_DOWN_SHIFT_PCT). The content is a column (pot → cards → meta)
  // centered via translateY(-50%), so the community cards land at the table
  // center.
  top: shiftDown(PORTRAIT_BOARD_CENTER_PCT),
  left: '10%',
  right: '10%',
  transform: 'translateY(-50%)',
  display: 'flex',
  justifyContent: 'center',
  ...TABLE_CONTENT_LAYER
};

const portraitCardsAndPotCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.1rem'
};

// Portrait board anchor: only the community cards are in flow (so their position
// is fixed at the table center). The Pot and TableDetails are absolutely
// positioned relative to this wrapper, so their size never shifts the cards.
const portraitBoardWrap: CSSProperties = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

// Pot floats above the board: bottom pinned to the cards' top edge, so adding
// side pots grows the stack upward instead of pushing the cards down. The
// margin is the pot's share of the felt's vertical rhythm — with the watermark
// now capped at 52% of the inner felt, this is also the clearance that keeps
// the pot pill off the logo's top edge (EN-10520).
const portraitPotAbove: CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '0.5rem'
};

// Same slot, lifted, while a fork is on the felt: the winner banner rides the
// top branch row and stands ~19px taller than one, and that spill has to land
// on felt instead of on the board below. 1.5rem clears the tallest case (three
// runs) with a few px to spare; the banner then parks itself under whatever
// this leaves (BANNER_POT_GAP).
const portraitPotAboveFork: CSSProperties = {
  ...portraitPotAbove,
  marginBottom: '1.5rem'
};

// The under-board felt group (table/hand number, then blinds / game type).
// Matches portraitPotAbove's spacing so the felt reads evenly above and below
// the board. A column, so the multi-run vote line (EN-10856) stacks between the
// board and the felt meta, as it does in landscape, instead of sharing a row
// that is only as wide as the board.
const portraitDetailsBelow: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25rem',
  marginTop: '0.5rem'
};

const portraitWinnerMessageStyle: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: shiftDown(52),
  transform: 'translateX(-50%)',
  zIndex: TableZ.winnerBanner
};

/* ── Rotating felt border glow ── */

// Inset within the OVAL (not the container), so it rides the oval's own shift.
const feltGlowStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(5% - 2px)',
  left: 'calc(2% - 2px)',
  right: 'calc(2% - 2px)',
  bottom: 'calc(5% - 1px)',
  borderRadius: '999px',
  opacity: 0.8,
  zIndex: 1,
  // Clips the rotating square child to the oval rim shape — this is what keeps
  // the glow correct at any size / aspect ratio.
  overflow: 'hidden'
};

// The spinning gradient layer. Square + centred so a transform rotation sweeps
// the conic colours around the rim; sized in the component to cover the oval.
const feltGlowSpinnerStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  willChange: 'transform',
  pointerEvents: 'none'
};

/* ── Table edge/apron ── */

const apronStyle: CSSProperties = {
  position: 'absolute',
  top: `calc(${shiftDown(20)} + 12px)`,
  left: 'calc(12% - 2px)',
  right: 'calc(12% - 2px)',
  bottom: `calc(${shiftUp(20)} - 12px)`,
  borderRadius: '999px',
  background: 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 40%, #050505 100%)',
  border: '2px solid #222',
  boxShadow: '0 6px 20px rgba(0,0,0,0.8)',
  transform: 'rotateX(10deg)',
  transformStyle: 'preserve-3d' as any,
  zIndex: 0
};

const portraitApronStyle: CSSProperties = {
  position: 'absolute',
  top: `calc(${shiftDown(5)} + 10px)`,
  left: 'calc(10% - 2px)',
  right: 'calc(10% - 2px)',
  bottom: `calc(${shiftUp(15)} - 10px)`,
  borderRadius: '999px',
  background: 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 40%, #050505 100%)',
  border: '2px solid #222',
  boxShadow: '0 6px 20px rgba(0,0,0,0.8)',
  transform: 'rotateX(12deg)',
  transformStyle: 'preserve-3d' as any,
  zIndex: 0
};

/* ── 3D Perspective wrappers ── */

/**
 * The felt's 3D tilt, and why its perspective cannot be a fixed pixel distance.
 *
 * `perspective` is a distance from the viewer to the z=0 plane, so what a
 * projection actually looks like depends on the ratio of the ELEMENT'S SIZE to
 * that distance — not on either alone. With a constant 400/600px, a bigger felt
 * gets a progressively stronger projection: its far (top) edge recedes further
 * and is pulled down the screen.
 *
 * The seats are NOT in this wrapper. They sit in the container at flat
 * percentage positions, so they do not recede with the felt — and past a certain
 * size the top seats visibly detach from the rail and float above the table.
 * Reported on a 3072px-wide display; measured there, the oval's top edge is
 * pulled ~314px down against ~129px on a 1920, which is the gap the seats were
 * sitting in.
 *
 * Scaling the distance with the container makes the projection scale-invariant:
 * every length in the scene, including the element's own height and its offset
 * from `perspectiveOrigin`, grows by the same factor as the perspective, so the
 * felt renders identically at every size and only gets bigger. Which is exactly
 * what "the same layout, but wider" means.
 *
 * The ratios are the design sizes the tilt was tuned at (700 tall landscape,
 * 900 portrait), so at those sizes this resolves to the original 400px / 600px
 * and nothing moves.
 */
const LANDSCAPE_PERSPECTIVE_RATIO = 400 / 700;
const PORTRAIT_PERSPECTIVE_RATIO = 600 / 900;

export const feltPerspectivePx = (containerHeight: number, isPortrait: boolean): number => {
  const ratio = isPortrait ? PORTRAIT_PERSPECTIVE_RATIO : LANDSCAPE_PERSPECTIVE_RATIO;
  // Before the first measurement, fall back to the design size rather than 0 —
  // a zero perspective is a degenerate projection, not a flat one.
  const height = containerHeight > 0 ? containerHeight : isPortrait ? 900 : 700;
  return height * ratio;
};

/**
 * One wrapper for both orientations now — they differed only in the perspective
 * distance, which is computed per-render by `feltPerspectivePx`.
 */
const perspectiveWrap: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  perspectiveOrigin: '50% 100%',
  zIndex: 1,
  pointerEvents: 'none'
};

/* ── Shared ── */

const innerFeltStyle: CSSProperties = {
  position: 'absolute',
  top: '5%',
  left: '2%',
  right: '2%',
  bottom: '5%',
  borderRadius: '999px',
  background: `
    radial-gradient(ellipse at 50% 40%, #2a6b45 0%, #1d5535 30%, #174a2d 55%, #0f3820 80%, #0a2a18 100%)
  `,
  border: 'none',
  boxShadow: 'inset 0 -10px 30px rgba(0,0,0,0.2), inset 0 10px 20px rgba(255,255,255,0.02)',
  zIndex: 2,
  overflow: 'hidden'
};

const tableLogoStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  // Percentage of the inner felt, CAPPED at the asset's native width so a wide
  // desktop table never upscales (and softens) the 200x87 PNG — see
  // feltTypography.feltLogoWidth.
  width: feltLogoWidth(false),
  height: 'auto',
  opacity: 0.35,
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 1,
  // The source logo is warm orange, which disappears against the Texas Orange
  // theme. Strip the source colour to pure white, then let `overlay` blend it
  // with the felt — that gives a readable watermark on every theme without
  // per-theme tuning (lightens dark felts, darkens light ones).
  filter: 'brightness(0) invert(1)',
  mixBlendMode: 'overlay'
};

// Portrait: the felt is much narrower, so 32% renders too small to read. 52% of
// the inner felt keeps the watermark anchored while leaving its top edge clear
// of the pot pill floating above the board (EN-10520 requirement: the pot must
// never touch the logo) — the old 60% put the two within a couple of px at
// 390px. Still capped at the asset's native width.
const portraitTableLogoStyle: CSSProperties = {
  ...tableLogoStyle,
  width: feltLogoWidth(true)
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: '#000000',
  opacity: 0,
  zIndex: 5,
  pointerEvents: 'none'
};

// The flat, centered HJ chip loader that used to live here now lives in
// ./TableLoadIndicator, which sizes it for the surface it is loading.
